import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { customInputsBulkQuery } from "#modules/quoteRequestLines/infra/customInputsBulk.query";
import {
  getQuoteRequestLineCustomInputsQueryData,
  quoteRequestLineCustomInputsQuery,
  setQuoteRequestLineCustomInputsQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLineCustomInputs.query";
import { useBulkQueryCache } from "#shared/hooks/useBulkQueryCache";

export const useCustomInputsBulk = (
  { quoteRequestId }: { quoteRequestId: string | undefined },
  options?: {
    enabled?: boolean;
  },
) => {
  const isBulkEnabled = !!quoteRequestId && options?.enabled !== false;

  const {
    data,
    isInitialLoading: isBulkQueryInitialLoading,
    error,
  } = useQuery({
    ...customInputsBulkQuery(quoteRequestId),
    enabled: isBulkEnabled,
  });

  const { isInitialLoading } = useBulkQueryCache({
    isBulkLoading: isBulkQueryInitialLoading,
    bulkData: data,
    isBulkEnabled,
    onPopulateCache: (bulkData, { queryClient, populatedKeys }) => {
      Object.entries(bulkData).forEach(([quoteRequestLineId, customInputs]) => {
        if (!populatedKeys.has(quoteRequestLineId)) {
          const existingData = getQuoteRequestLineCustomInputsQueryData(
            queryClient,
            quoteRequestLineId,
          );

          if (!existingData) {
            setQuoteRequestLineCustomInputsQueryData(queryClient, quoteRequestLineId, customInputs);
          }

          populatedKeys.add(quoteRequestLineId);
        }
      });
    },
  });

  const customInputsQueries = useQueries({
    queries: Object.keys(data ?? {}).map((quoteRequestLineId) => ({
      ...quoteRequestLineCustomInputsQuery(quoteRequestLineId),
      enabled: !isInitialLoading,
    })),
  });

  const customInputs = useMemo(
    () => customInputsQueries.map((q) => q.data).filter(Boolean),
    [customInputsQueries],
  );

  return { customInputs, isInitialLoading, error };
};
